import { Routes, Route, useLocation, Navigate } from 'react-router';
import { Suspense, lazy, useEffect } from 'react';
import { ToastContainer, useToast } from '@mieweb/ui';
import { AuthProvider } from './lib/auth-context';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { isDesktop } from './lib/desktop';

// Eagerly-loaded static pages (pre-rendered, no loading flash)
import { LandingPage } from './pages/LandingPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { DocsPage } from './pages/DocsPage';
import { ChangelogPage } from './pages/ChangelogPage';
import { DownloadPage } from './pages/DownloadPage';
import { ComparePage } from './pages/ComparePage';
import { ComparisonPage } from './pages/ComparisonPage';
import { PricingPage } from './pages/PricingPage';
import { AboutPage } from './pages/AboutPage';
import { DevelopersPage } from './pages/DevelopersPage';
import { AiPage } from './pages/AiPage';
import { ArticlePage } from './pages/ArticlePage';
import { CollectionIndexPage } from './pages/CollectionIndexPage';
import { NotFoundPage } from './pages/NotFoundPage';

// Lazy-loaded pages (require auth, ok to show loading)
const DashboardPage = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const InboxesPage = lazy(() => import('./pages/InboxesPage').then(m => ({ default: m.InboxesPage })));
const InboxDetailPage = lazy(() => import('./pages/InboxDetailPage').then(m => ({ default: m.InboxDetailPage })));
const FeedbackDetailPage = lazy(() => import('./pages/FeedbackDetailPage').then(m => ({ default: m.FeedbackDetailPage })));
const FeedbackBoardPage = lazy(() => import('./pages/FeedbackBoardPage').then(m => ({ default: m.FeedbackBoardPage })));
const WidgetsPage = lazy(() => import('./pages/WidgetsPage').then(m => ({ default: m.WidgetsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const BrandsPage = lazy(() => import('./pages/BrandsPage').then(m => ({ default: m.BrandsPage })));
const BrandEditorPage = lazy(() => import('./pages/BrandEditorPage').then(m => ({ default: m.BrandEditorPage })));

const InvitePage = lazy(() => import('./pages/InvitePage').then(m => ({ default: m.InvitePage })));
const JoinPage = lazy(() => import('./pages/JoinPage').then(m => ({ default: m.JoinPage })));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage').then(m => ({ default: m.OAuthConsentPage })));

// Feedback Pages
const FeedbackPagesPage = lazy(() => import('./pages/FeedbackPagesPage').then(m => ({ default: m.FeedbackPagesPage })));
const FeedbackPageEditorPage = lazy(() => import('./pages/FeedbackPageEditorPage').then(m => ({ default: m.FeedbackPageEditorPage })));
const PresenterPage = lazy(() => import('./pages/PresenterPage').then(m => ({ default: m.PresenterPage })));

const PublicFeedbackPage = lazy(() => import('./pages/PublicFeedbackPage').then(m => ({ default: m.PublicFeedbackPageView })));
const PublicResultsPage = lazy(() => import('./pages/PublicResultsPage').then(m => ({ default: m.PublicResultsPage })));

// Admin pages
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));
const AdminOrganizationsPage = lazy(() => import('./pages/admin/AdminOrganizationsPage').then(m => ({ default: m.AdminOrganizationsPage })));
const AdminOrgDetailPage = lazy(() => import('./pages/admin/AdminOrgDetailPage').then(m => ({ default: m.AdminOrgDetailPage })));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function ThemeFaviconSync() {
  useEffect(() => {
    const favicon = document.getElementById('app-favicon') as HTMLLinkElement | null;
    if (!favicon) return;

    const updateFavicon = () => {
      const root = document.documentElement;
      const isDark = root.classList.contains('dark') || root.getAttribute('data-theme') === 'dark';
      favicon.href = isDark ? '/favicon-dark.svg' : '/favicon-light.svg';
    };

    updateFavicon();

    const observer = new MutationObserver(updateFavicon);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });

    window.addEventListener('storage', updateFavicon);

    return () => {
      observer.disconnect();
      window.removeEventListener('storage', updateFavicon);
    };
  }, []);

  return null;
}

// Protected layout wrapper
function ProtectedLayout() {
  return (
    <ProtectedRoute requireOrganization>
      <AppLayout />
    </ProtectedRoute>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function GlobalToastContainer() {
  const { toasts, dismiss } = useToast();
  return <ToastContainer toasts={toasts} onDismiss={dismiss} position="bottom-right" />;
}

export function App() {
  return (
    <AuthProvider>
      <ScrollToTop />
      <ThemeFaviconSync />
      <GlobalToastContainer />
      <Routes>
        {/* Static pre-rendered routes - no Suspense, instant hydration */}
        {/* Desktop app skips the marketing site and opens straight to login. */}
        <Route path="/" element={isDesktop ? <Navigate to="/login" replace /> : <LandingPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/vs" element={<ComparePage />} />
        <Route path="/vs/:slug" element={<ComparisonPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/ai" element={<AiPage />} />
        <Route path="/guides" element={<CollectionIndexPage collection="guides" />} />
        <Route path="/guides/:slug" element={<ArticlePage collection="guides" />} />
        <Route path="/use-cases" element={<CollectionIndexPage collection="use-cases" />} />
        <Route path="/use-cases/:slug" element={<ArticlePage collection="use-cases" />} />
        <Route path="/alternatives" element={<CollectionIndexPage collection="alternatives" />} />
        <Route path="/alternatives/:slug" element={<ArticlePage collection="alternatives" />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* OAuth2 consent screen (Pollenate as provider). Handles its own auth
            redirect so the full authorization query survives a login bounce. */}
        <Route path="/oauth/authorize" element={
          <Suspense fallback={<LoadingFallback />}>
            <OAuthConsentPage />
          </Suspense>
        } />

        {/* Public feedback pages (no auth) */}
        <Route path="/f/:orgSlug/:pageSlug" element={
          <Suspense fallback={<LoadingFallback />}>
            <PublicFeedbackPage />
          </Suspense>
        } />
        <Route path="/f/:orgSlug/:pageSlug/results" element={
          <Suspense fallback={<LoadingFallback />}>
            <PublicResultsPage />
          </Suspense>
        } />
        
        {/* Dynamic routes - wrapped in Suspense for lazy loading */}
        <Route path="/invite/:token" element={
          <Suspense fallback={<LoadingFallback />}>
            <InvitePage />
          </Suspense>
        } />
        <Route path="/join/:code" element={
          <Suspense fallback={<LoadingFallback />}>
            <JoinPage />
          </Suspense>
        } />
        
        {/* Onboarding (needs auth but not org) */}
        <Route
          path="/onboarding"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            </Suspense>
          }
        />
        
        {/* Protected routes with layout */}
        <Route element={
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedLayout />
          </Suspense>
        }>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/board" element={<FeedbackBoardPage />} />
          <Route path="/inboxes" element={<InboxesPage />} />
          <Route path="/inboxes/:inboxId" element={<InboxDetailPage />} />
          <Route path="/inboxes/:inboxId/feedback/:feedbackId" element={<FeedbackDetailPage />} />
          <Route path="/widgets" element={<WidgetsPage />} />
          <Route path="/feedback-pages" element={<FeedbackPagesPage />} />
          <Route path="/feedback-pages/:pageId" element={<FeedbackPageEditorPage />} />
          <Route path="/feedback-pages/:pageId/present" element={<PresenterPage />} />
          <Route path="/brands" element={<BrandsPage />} />
          <Route path="/brands/:brandId" element={<BrandEditorPage />} />
          <Route path="/api-keys" element={<SettingsPage />} />
          <Route path="/automations" element={<SettingsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          
          {/* Admin routes */}
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/admin/organizations" element={<AdminOrganizationsPage />} />
          <Route path="/admin/organizations/:orgId" element={<AdminOrgDetailPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
        </Route>

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthProvider>
  );
}
